import { useTranslation } from 'react-i18next'
import {
  Card,
  Text,
  TableColumns,
  TableColumnsColumnProps,
  Tag,
} from '@mercai/clever'

import { ResultsStepProps, ResultRowProps } from './results.type'

import styles from './results.module.css'

export const ResultsStep = ({ data }: ResultsStepProps) => {
  const { t } = useTranslation('screenAiCenter')

  if (!data) {
    return (
      <div className={styles.core}>
        <Text variant="text-base">{t('conversationNewResultsNoData')}</Text>
      </div>
    )
  }

  const successRate = data.total > 0 ? (data.success / data.total) * 100 : 0

  const tableData: ResultRowProps[] = data?.results?.map(
    (
      result: {
        phone: string
        variables: Record<string, string>
        success: boolean
        error?: string
      },
      index: number,
    ) => ({
      id: index.toString(),
      phone: result.phone,
      status: result.success ? ('success' as const) : ('failed' as const),
      variables: Object.entries(result.variables)
        .map(([key, value]) => `${key}: ${value}`)
        .join(', '),
      error: result.error || '-',
    }),
  )

  const columns: TableColumnsColumnProps<ResultRowProps>[] = [
    {
      key: 'phone',
      dataKey: 'phone',
      title: t('conversationNewResultsTablePhone'),
      render: ({ value }) => <Text variant="text-base">{value}</Text>,
    },
    {
      key: 'status',
      dataKey: 'status',
      title: t('conversationNewResultsTableStatus'),
      render: ({ record }) => {
        const isSuccess = record.status === 'success'
        return (
          <div className={styles['status-cell']}>
            <Tag variant={isSuccess ? 'success' : 'danger'} model="light">
              {isSuccess
                ? t('conversationNewResultsStatusSuccess')
                : t('conversationNewResultsStatusFailed')}
            </Tag>
          </div>
        )
      },
    },
  ]

  return (
    <div className={styles.core}>
      <div className={styles['summary-grid']}>
        <Card className={styles['summary-card']}>
          <div className={styles['summary-content']}>
            <Text variant="text-small" className={styles['summary-label']}>
              {t('conversationNewResultsSuccess')}
            </Text>
            <Text variant="title-large" className={styles['summary-value']}>
              {data.success}
            </Text>
          </div>
        </Card>

        <Card className={styles['summary-card']}>
          <div className={styles['summary-content']}>
            <Text variant="text-small" className={styles['summary-label']}>
              {t('conversationNewResultsFailed')}
            </Text>
            <Text variant="title-large" className={styles['summary-value']}>
              {data.failed}
            </Text>
          </div>
        </Card>

        <Card className={styles['summary-card']}>
          <div className={styles['summary-content']}>
            <Text variant="text-small" className={styles['summary-label']}>
              {t('conversationNewResultsTotal')}
            </Text>
            <Text variant="title-large" className={styles['summary-value']}>
              {data.total}
            </Text>
          </div>
        </Card>

        <Card className={styles['summary-card']}>
          <div className={styles['summary-content']}>
            <Text variant="text-small" className={styles['summary-label']}>
              {t('conversationNewResultsSuccessRate')}
            </Text>
            <Text variant="title-large" className={styles['summary-value']}>
              {successRate.toFixed(1)}%
            </Text>
          </div>
        </Card>
      </div>

      <div className={styles['table-container']}>
        <Text variant="title-base">
          {t('conversationNewResultsDetailedResults')}
        </Text>

        <TableColumns<ResultRowProps> columns={columns} data={tableData} />
      </div>
    </div>
  )
}
